import React, {createContext, PropsWithChildren, useContext, useEffect} from 'react';
import {useInspectionsMapContext} from './InspectionsMapContext';
import {useMapEvents} from 'react-leaflet';
import {LatLngBounds} from 'leaflet';
import * as _ from 'lodash';
import {useCitiesContext, useStreetsContext} from '../../_common/components/CitiesAndStreets';
import {InspectionsMapAPI} from '../api';
import {Option} from '../../_common/types';

type MapHandlingContext = {}

const MapHandlingContext = createContext<MapHandlingContext>({});

export const useMapHandlingContext = () => useContext(MapHandlingContext);

interface MapHandlingProviderProps extends PropsWithChildren<any> {}

export const MapHandlingProvider = ({children}: MapHandlingProviderProps) => {
  const {updateBounds} = useInspectionsMapContext();
  const {cities} = useCitiesContext() as {cities: Option[]};
  const {streets} = useStreetsContext() as {streets: Option[]};
  const api = new InspectionsMapAPI();

  const qs = new URLSearchParams(window.location.search);
  const fromLatitude = parseFloat(qs.get('fromLatitude') || '');
  const fromLongitude = parseFloat(qs.get('fromLongitude') || '');
  const toLatitude = parseFloat(qs.get('toLatitude') || '');
  const toLongitude = parseFloat(qs.get('toLongitude') || '');
  const cityID = parseFloat(qs.get('cityID') || '');
  const streetID = parseFloat(qs.get('streetID') || '');

  const map = useMapEvents({
    loading: async () => {
      // doesn't work correctly for some reason
      const bounds = map.getBounds();
      await updateBounds(
        bounds.getSouth(),
        bounds.getEast(),
        bounds.getNorth(),
        bounds.getWest()
      );
    },
    zoomend: async () => {
      const bounds = map.getBounds();
      await updateBounds(
        bounds.getSouth(),
        bounds.getWest(),
        bounds.getNorth(),
        bounds.getEast()
      );
    },
    moveend: async () => {
      const bounds = map.getBounds();
      await updateBounds(
        bounds.getSouth(),
        bounds.getWest(),
        bounds.getNorth(),
        bounds.getEast()
      );
    },
  });

  useEffect(() => {
    if (fromLongitude && fromLongitude && toLongitude && toLatitude) {
      map.fitBounds(new LatLngBounds({lat: fromLatitude, lng: fromLongitude}, {lat: toLatitude, lng: toLongitude}));
      updateBounds(
        fromLatitude,
        fromLongitude,
        toLatitude,
        toLongitude
      ).catch(console.error);
    }
  }, []);

  useEffect(() => {
    if (_.isFinite(cityID) && cityID > 0) {
      getCityCoords(cityID, streetID)
        .then(coords => {
          map.flyTo(coords, 15);
        })
        .catch(console.error);
    }
  }, [cityID, streetID]);

  const getCityCoords = async (cityID: number, streetID: number) => {
    let addressParts: string[] = [];
    const [city] = cities.filter(city => parseInt(city.value, 10) === cityID);
    if (city) {
      addressParts = [...addressParts, city.label];

      if (_.isFinite(streetID) && streetID > 0) {
        const [street] = streets.filter(street => parseInt(street.value, 10) === streetID);
        addressParts = [...addressParts, street?.label ?? ''];
      }
    }
    const address = addressParts.filter(Boolean).join(' ');

    return api.getCoordsByAddress(address);
  };

  return (
    <MapHandlingContext.Provider value={{}}>
      {children}
    </MapHandlingContext.Provider>
  );
};
